import { useState } from 'react'
import { parseLogEvent } from '../../search'

/**
 * The raw-event viewer: parsed by default, findable, copyable.
 *
 * Most raw lines are `key=value` grammars or JSON, so the default view is the
 * PARSED table — the readable form is the point; the verbatim line stays one
 * click away. Find is view-aware: in parsed view it FILTERS the pairs (by key
 * or value), in raw view it HIGHLIGHTS matches, because "which rows mention
 * 10.0.3.21" and "where in the blob is it" are different questions.
 */
const RAW_VIEW_PREF = 'bloo.search.rawview'

export function RawEventView({ text }: { text: string }) {
  const [q, setQ] = useState('')
  const parsed = parseLogEvent(text)
  // The parsed/raw choice is a working style, not a per-event decision —
  // remembered so a raw-format reader lands on the native line every time.
  const [view, setView] = useState<'parsed' | 'raw'>(() => {
    if (parsed.kind === 'text') return 'raw'
    try {
      return window.localStorage.getItem(RAW_VIEW_PREF) === 'raw' ? 'raw' : 'parsed'
    } catch {
      return 'parsed'
    }
  })
  const chooseView = (v: 'parsed' | 'raw') => {
    setView(v)
    try {
      window.localStorage.setItem(RAW_VIEW_PREF, v)
    } catch {
      // Preference only; losing it costs one extra click next time.
    }
  }

  const needle = q.trim().toLowerCase()

  /** Raw text with case-insensitive matches wrapped in <mark>. */
  const highlighted = (source: string) => {
    if (!needle) return source
    const out: Array<string | React.ReactElement> = []
    let i = 0
    const lower = source.toLowerCase()
    for (let hit = lower.indexOf(needle); hit !== -1; hit = lower.indexOf(needle, i)) {
      if (hit > i) out.push(source.slice(i, hit))
      out.push(<mark key={hit}>{source.slice(hit, hit + needle.length)}</mark>)
      i = hit + needle.length
    }
    out.push(source.slice(i))
    return out
  }

  const matchCount = needle
    ? (text.toLowerCase().split(needle).length - 1)
    : null

  const pairs =
    parsed.kind === 'kv'
      ? parsed.pairs.filter(
          ([k, v]) => !needle || k.toLowerCase().includes(needle) || v.toLowerCase().includes(needle),
        )
      : []

  return (
    <div className="rev">
      <div className="rev-bar">
        <input
          className="rev-find"
          aria-label="Find in event"
          placeholder="find in event…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        {needle ? (
          <span className="rev-count" role="status">
            {view === 'parsed' && parsed.kind === 'kv'
              ? `${pairs.length} of ${parsed.pairs.length} fields`
              : `${matchCount} match${matchCount === 1 ? '' : 'es'}`}
          </span>
        ) : null}
        {parsed.kind !== 'text' ? (
          <span aria-label="View mode">
            {(['parsed', 'raw'] as const).map((v) => (
              <button
                key={v}
                type="button"
                className="list-chip"
                aria-pressed={view === v}
                onClick={() => chooseView(v)}
              >
                {v}
              </button>
            ))}
          </span>
        ) : null}
      </div>

      {view === 'parsed' && parsed.kind === 'kv' ? (
        pairs.length ? (
          <dl className="rev-kv">
            {pairs.map(([k, v], i) => (
              <div key={`${k}-${i}`} style={{ display: 'contents' }}>
                <dt>{highlighted(k)}</dt>
                <dd>{highlighted(v)}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="hud-unit" role="status">No fields match.</p>
        )
      ) : view === 'parsed' && parsed.kind === 'json' ? (
        <pre className="raw-log">{highlighted(parsed.pretty)}</pre>
      ) : (
        <pre className="raw-log">{highlighted(text)}</pre>
      )}
    </div>
  )
}
